import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import uniqueId from 'lodash/uniqueId';

import { useDeleteActionConditionMutation } from 'api/main';
import { customTunnelSelectors } from 'reducers';
import {
    editingConditionIdSet,
    editingConditionSet,
    newEditingConditionIdSet,
} from 'reducers/customTunnel';
import { conditionType } from 'utils/constants';
import { useDialog } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import ConditionForm from './ConditionForm';
import { ConfirmDialog } from 'components';
import { Box, Collapse, Paper, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';

const iconSx = { cursor: 'pointer' };

const conditionLabels = {
    [conditionType.LESS_THAN]: texts.ALERTS.LESS_THAN,
    [conditionType.GREATER_THAN]: texts.ALERTS.GREATER_THAN,
    [conditionType.EQUAL]: texts.ALERTS.EQUAL,
};

const ConditionItem = ({
    condition,
    pmConditions,
    onRemove,
    index,
    formCleanup,
    editingAction,
}) => {
    const uId = useRef(uniqueId()); // unique id used for editing state
    const dispatch = useDispatch();

    const { ppId: editingConditionId } = useSelector(customTunnelSelectors.getEditingConditionId);
    const newEditingConditionId = useSelector(customTunnelSelectors.getNewEditingConditionId);

    const { open: confirmEditOpen, onClose: handleEditClose, onOpen: handleEditOpen } = useDialog();
    const { open: deleteOpen, onClose: handleDeleteClose, onOpen: handleDeleteOpen } = useDialog();

    const [deleteCondition, { isLoading: deleteLoading, isSuccess: deleteSuccessful }] =
        useDeleteActionConditionMutation();

    const handleDeleteCondition = useCallback(() => {
        if (condition?.id) {
            deleteCondition({ ppId: condition.id, pmId: condition?.pmId });
        } else {
            onRemove();
        }
    }, [condition.id, condition?.pmId, deleteCondition, onRemove]);

    const handleEdit = useCallback(() => {
        // condition form is kept in store
        dispatch(editingConditionSet({ value: condition }));
        // close the confirm edit dialog
        handleEditClose();
        // cleanup the invalid conditions
        formCleanup();
        // set the current condition as editing
        if (condition?.id) {
            // editing an existing condition
            dispatch(
                editingConditionIdSet({
                    ppId: condition?.id,
                    pmId: pmConditions?.find(c => c.type === condition?.type)?.id, // match conditions by type since it is unique per action
                })
            );
            dispatch(newEditingConditionIdSet(null));
        } else {
            // editing a condition that is not saved in DB
            dispatch(newEditingConditionIdSet(uId.current));
            dispatch(editingConditionIdSet(null));
        }
    }, [dispatch, formCleanup, handleEditClose, pmConditions, condition]);

    const handleConfirmEdit = useCallback(() => {
        if (editingConditionId) {
            handleEditOpen();
        } else {
            handleEdit();
        }
    }, [editingConditionId, handleEditOpen, handleEdit]);

    const handleCancel = useCallback(() => {
        dispatch(editingConditionIdSet(null));
        dispatch(newEditingConditionIdSet(null));
        dispatch(editingConditionSet(null));
    }, [dispatch]);

    useEffect(() => {
        if (deleteSuccessful) {
            handleDeleteClose();
        }
    }, [deleteSuccessful, handleDeleteClose]);

    useEffect(() => {
        if (condition?.limitValue === '') {
            // set newly added condition without a limitValue as being edited
            dispatch(newEditingConditionIdSet(uId.current));
            dispatch(editingConditionSet({ value: condition }));
        }
    }, [dispatch, condition]);

    return (
        <Paper
            sx={theme => ({
                padding: 1,
                borderRadius: '3px',
                marginY: 1,
                border:
                    newEditingConditionId === uId.current || editingConditionId === condition?.id
                        ? `2px solid ${theme.palette.grey[500]}`
                        : '',
                backgroundColor:
                    newEditingConditionId !== uId.current && editingConditionId !== condition?.id
                        ? theme.palette.grey[200]
                        : 'initial',
            })}
            elevation={3}
        >
            {newEditingConditionId !== uId.current && editingConditionId !== condition?.id && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography noWrap sx={{ flex: '1 1 50%' }}>
                        {conditionLabels[condition.type] + ' ' + condition.limitValue}
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <ConfirmDialog
                            open={deleteOpen}
                            handleConfirm={handleDeleteCondition}
                            handleClose={handleDeleteClose}
                            title={texts.DELETE_QUESTION}
                            loading={deleteLoading}
                            confirmText={texts.CONFIRMER}
                            cancelText={texts.CANCEL}
                        >
                            {texts.CONFIRM_DELETE_QUESTION}
                        </ConfirmDialog>
                        <ConfirmDialog
                            open={confirmEditOpen}
                            handleConfirm={handleEdit}
                            handleClose={handleEditClose}
                            title={texts.CHANGES_NOT_CONFIRMED}
                            confirmText={texts.CONFIRMER}
                            cancelText={texts.CANCEL}
                        >
                            {texts.CONFIRM_DISCARD_CHANGES}
                        </ConfirmDialog>
                        <icons.Pencil
                            sx={{ ...iconSx, '&:hover': { color: 'primary.main' } }}
                            onClick={handleConfirmEdit}
                        />
                        <icons.Delete
                            sx={{ ...iconSx, '&:hover': { color: 'error.main' } }}
                            onClick={handleDeleteOpen}
                        />
                    </Box>
                </Box>
            )}
            <Collapse
                in={newEditingConditionId === uId.current || editingConditionId === condition?.id}
            >
                {(newEditingConditionId === uId.current ||
                    editingConditionId === condition?.id) && (
                    <ConditionForm
                        index={index}
                        condition={condition}
                        editingAction={editingAction}
                        onCancel={handleCancel}
                    />
                )}
            </Collapse>
        </Paper>
    );
};

ConditionItem.propTypes = {
    condition: PropTypes.object.isRequired,
    onRemove: PropTypes.func.isRequired,
    pmConditions: PropTypes.array,
    index: PropTypes.number,
    formCleanup: PropTypes.func,
    editingAction: PropTypes.object,
};

export default ConditionItem;
